import React from 'react'
import { Typography, useMediaQuery, Drawer, Stack, IconButton, Box, Grid, SvgIcon } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import { getEnvVariable } from '@/app/service/getenvService'
import Link from 'next/link'
import { getDateStr } from '@utilities/dateFormatter'
import DOMPurify from 'dompurify'

import { Strings } from '@/lib/constants/strings'
import { TypographyVariants } from '@/app/styles/themes/typography'
import { AISuggestIcon } from '@/public/icons/bi_icons'
import { analyticsTags } from '@/app/lib/analytics/analyticsEventTags'
import { pushToDataLayer } from '@/app/lib/analytics/analyticsTracking'
import { DrawerArrowBack } from '@/public/icons/menu_item'
import { useCategoryDetails } from '@/lib/hooks/useCategoryDetails'
import { sendAIAssistants, sendAIAssistantsChat } from '@/app/service/experienceService'
import { useSession } from 'next-auth/react'
import { useCompanyContext } from '@/lib/context/companyContext'
import { SendAIAssistantsRequest } from '@/app/service/models/aiAssistants'
import parse from 'html-react-parser'
import { useFlags } from 'launchdarkly-react-client-sdk'
import CustomBackdrop from '../chart/customBackdrop'
import CustomChips from '../chips'
import CircularIndeterminate from '../circularprogress'
import { AIQuestionBubble } from './aiQuestionBubble'
import AIAssistantHistoryContainer from './aiAssistantHistoryContainer'
import AIErrorBubble from './aiErrorBubble'

interface AIAssistantDrawerProps {
	isOpen?: boolean
	onClose?: (event: object, reason: 'backdropClick' | 'escapeKeyDown') => void
	title: string
	drawerWidth?: string
}
export default function AIAssistantDrawer({
	isOpen = true,
	onClose = () => {},
	title = '',
	drawerWidth = '395px',
}: Readonly<AIAssistantDrawerProps>) {
	const theme = useTheme()
	const session = useSession()
	const { selectedCompanyId: companyId } = useCompanyContext()
	const isMobile = useMediaQuery('(max-width:899px)')
	const { tempBoss2828PromptConstructionInExpApi } = useFlags()
	const dummy_message_1 = 'You can ask the assistant about your business performance, goals, or transactions.'
	const dummy_message_2 = 'What would you like to learn more about?'
	const [privacyNoticeURL, setPrivacyNoticeURL] = React.useState<string>('#')

	const [selectedCategory, setSelectedCategory] = React.useState<string>('')
	const [visibleQuestionsCount, setVisibleQuestionsCount] = React.useState<number>(3)
	const [loadingMoreQuestions, setLoadingMoreQuestions] = React.useState<boolean>(false)

	type Message = {
		id: string
		prompt: string
		response: string
		isLoading?: boolean
		error?: boolean
		errorText?: string
	}

	const [messages, setMessages] = React.useState<Message[]>([])

	// Initialize the hook
	const { categoryDetail, loading, error, fetchCategoryDetails } = useCategoryDetails()

	const { biAIAssistedCategorySelection, biAIAssistedSuggestMoreQuestions, biAIAssistedPrivacyNoticeURL } =
		analyticsTags

	const categoryList = [
		{ label: 'Brokerage brief' },
		{ label: 'GCI performance' },
		{ label: 'Historical analysis' },
		{ label: 'Revenue attribution' },
	]

	const handleCategoryClick = (label: string) => {
		if (selectedCategory === label) return
		setMessages([])
		const newCategory = label
		setSelectedCategory(newCategory)
		setVisibleQuestionsCount(3)

		// Call API if a category is selected
		if (newCategory) {
			fetchCategoryDetails(newCategory)
		}

		pushToDataLayer({
			event: biAIAssistedCategorySelection.event,
			select_category: label,
		})
	}

	const handleSuggestMore = async () => {
		setLoadingMoreQuestions(true)

		// Add 1 second delay to show loading state
		await new Promise<void>(resolve => {
			setTimeout(() => resolve(), 1000)
		})

		setVisibleQuestionsCount(prev => prev + 3)
		setLoadingMoreQuestions(false)
		pushToDataLayer(biAIAssistedSuggestMoreQuestions)
	}

	React.useEffect(() => {
		const loadEnv = async () => {
			const url = await getEnvVariable('NEXT_PUBLIC_AI_PRIVACYNOTICE_URL')
			setPrivacyNoticeURL(url ?? '#')
		}

		loadEnv()
	}, [])

	const historyRef = React.useRef<HTMLDivElement | null>(null)
	const endRef = React.useRef<HTMLDivElement | null>(null)

	const NEAR_BOTTOM_THRESHOLD = 120

	const isNearBottom = () => {
		const el = historyRef.current
		if (!el) return false
		return Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight - NEAR_BOTTOM_THRESHOLD
	}

	React.useLayoutEffect(() => {
		if (isNearBottom()) {
			endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
		}
	}, [messages])

	type CloseReason = 'backdropClick' | 'escapeKeyDown'

	const closeDrawer = (event: object, reason: CloseReason) => {
		if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
			setSelectedCategory('')
			setMessages([])
		}
		onClose?.(event, reason)
	}
	const handleIconClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		closeDrawer(e, 'escapeKeyDown')
	}
	const handleDrawerClose = (event: object, reason: CloseReason) => {
		closeDrawer(event, reason)
	}

	const sanitizeHtml = (html: string): string => {
		return DOMPurify.sanitize(html, {
			USE_PROFILES: { html: true },
			ALLOWED_TAGS: [
				'p',
				'br',
				'strong',
				'em',
				'u',
				'ol',
				'ul',
				'li',
				'h1',
				'h2',
				'h3',
				'h4',
				'h5',
				'h6',
				'blockquote',
				'code',
				'pre',
			],
			ALLOWED_ATTR: ['class'],
			FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
			FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover', 'style'],
			KEEP_CONTENT: false,
		})
	}

	function genId() {
		const array = new Uint8Array(16) // 128 bits
		window.crypto.getRandomValues(array)
		return Array.from(array, b => b.toString(16).padStart(2, '0')).join('')
	}

	const handleQuestionClick = async (questionText: string) => {
		const id = genId()
		setMessages(prev => [...prev, { id, prompt: questionText, response: '', isLoading: true, error: false }])

		try {
			const today = new Date()
			const currentDateStr = getDateStr(today)

			const queryTx = tempBoss2828PromptConstructionInExpApi
				? questionText
				: `Given company GUID '${companyId}' and today's date '${currentDateStr}', ` +
					`return the response in HTML format (no markdown, no JSON). Question: ${questionText}`

			const payload: SendAIAssistantsRequest = {
				assistantTypeCd: 'BUSINTEL',
				partyId: companyId ?? '',
				partyTypeCd: 'BROKER',
				queryTx,
			}

			const resp = tempBoss2828PromptConstructionInExpApi ? await sendAIAssistantsChat(payload, session.data?.accessToken) : await sendAIAssistants(payload, session.data?.accessToken)
			const html = typeof resp === 'string' ? resp : (resp?.responseTx ?? '')

			const cleanHtml = sanitizeHtml(html || '')

			setMessages(prev =>
				prev.map(m => (m.id === id ? { ...m, response: cleanHtml, isLoading: false, error: false } : m))
			)
		} catch (err) {
			console.error(err)

			setMessages(prev =>
				prev.map(m =>
					m.id === id
						? {
								...m,
								response: '',
								isLoading: false,
								error: true,
								errorText: 'Something went wrong. Please refresh the page or try again later.',
							}
						: m
				)
			)
		}
	}

	const renderAssistantResponse = (m: Message) => {
		if (m.isLoading) {
			return <CircularIndeterminate marginLeft="6px" data-testid="spinner" />
		}

		if (m.error) {
			return (
				<AIErrorBubble
					text={m.errorText ?? 'Something went wrong. Please refresh the page or try again later.'}
					sx={{ mb: 3 }}
				/>
			)
		}

		return (
			<Box
				sx={{
					alignSelf: 'flex-start',
					maxWidth: { xs: '99%', md: '99%' },
					bgcolor: t => (t.palette.mode === 'dark' ? 'grey.900' : '#F8F8F8'),
					color: 'text.primary',
					borderRadius: 2,
					p: 2,
					boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
					mb: 3,
				}}
				data-testid="assistant-response"
			>
				<Box component="div">{parse(sanitizeHtml(m.response || ''))}</Box>
			</Box>
		)
	}

	return (
		<Drawer
			anchor={isMobile ? 'bottom' : 'right'}
			variant="temporary"
			open={isOpen}
			onClose={handleDrawerClose}
			slots={{ backdrop: CustomBackdrop }}
			sx={{
				'& .MuiDrawer-paper': {
					width: { xs: '100%', md: drawerWidth },
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
				},
			}}
			data-testid="ai-assistant-drawer"
			disableScrollLock={false}
		>
			<Stack
				direction="row"
				sx={{
					p: '8px 0',
					justifyContent: 'space-between',
					flexShrink: 0,
				}}
				alignItems="center"
			>
				{isMobile ? (
					<>
						<IconButton
							onClick={handleIconClick}
							sx={{
								color: theme.palette.foreground.neutral.onSurfaceVar01,
								pl: '20px',
							}}
							disableRipple
						>
							<DrawerArrowBack />
						</IconButton>
						<Typography
							variant={TypographyVariants.mediumSemiBold}
							sx={{
								color: theme.palette.foreground.neutral.onSurfaceVar01,
								textAlign: 'center',
								width: 'calc(100% - 50px)',
								paddingRight: '50px',
							}}
						>
							{title}
						</Typography>
					</>
				) : (
					<>
						<Typography
							variant={TypographyVariants.baseRegular}
							sx={{
								color: theme.palette.foreground.neutral.onSurfaceVar01,
								textAlign: 'center',
								width: 'calc(100% - 28px)',
								paddingLeft: '45px',
							}}
						>
							{title}
						</Typography>
						<IconButton
							onClick={handleIconClick}
							sx={{
								color: theme.palette.foreground.neutral.onSurfaceVar01,
								mr: 2,
								padding: '8px 8px 4px 8px',
							}}
							disableRipple
						>
							<CloseIcon />
						</IconButton>
					</>
				)}
			</Stack>

			<Box sx={{ flex: 1, overflowY: 'auto' }}>
				<Typography
					sx={{
						ml: 3,
						mr: 3,
						mb: 2,
						mt: 3,
						color: theme.palette.foreground.neutral.onSurfaceVar02,
						textAlign: 'center',
					}}
					variant={TypographyVariants.smallRegular}
				>
					{Strings.AI_ASSISTANT_DESCRIPTION_1}{' '}
					<Link
						href={privacyNoticeURL}
						target="_blank"
						rel="noopener noreferrer"
						onClick={() => {
							pushToDataLayer(biAIAssistedPrivacyNoticeURL)
						}}
						style={{ color: theme.palette.foreground.link.default }}
					>
						{Strings.AI_ASSISTANT_DESCRIPTION_PRIVACY_NOTICE}
					</Link>
					.
				</Typography>

				<Typography
					sx={{
						ml: 3,
						mr: 3,
						mb: 4,
						color: theme.palette.foreground.neutral.onSurfaceVar02,
						textAlign: 'center',
					}}
					variant={TypographyVariants.smallRegular}
				>
					{Strings.AI_ASSISTANT_DESCRIPTION_2}
				</Typography>

				<Box
					sx={{
						backgroundColor: theme.palette.background.default,
						mb: 2,
						ml: 3,
						p: 3,
						maxWidth: 'calc(100% - 75px)',
						borderRadius: '16px',
					}}
				>
					<Typography sx={{ mb: 2 }}>{dummy_message_1}</Typography>
					<Typography>{dummy_message_2}</Typography>
				</Box>

				<Box
					sx={{
						position: 'sticky',
						top: 0,
						zIndex: 10,
						backgroundColor: theme.palette.background.paper,
					}}
				>
					<Box sx={{ p: 3 }}>
						<Grid container spacing={2}>
							{categoryList.map(category => (
								<Grid item xs="auto" key={category.label}>
									<CustomChips
										label={category.label}
										selected={selectedCategory === category.label}
										onClick={() => handleCategoryClick(category.label)}
										dataTestId="suggested-question"
									/>
								</Grid>
							))}
						</Grid>
					</Box>
				</Box>

				{loading && selectedCategory && <CircularIndeterminate data-testid="spinner" />}

				{!loading && !error && categoryDetail && selectedCategory && (
					<>
						<Box
							sx={{
								ml: 3,
								p: 3,
								backgroundColor: theme.palette.background.default,
								maxWidth: 'calc(100% - 75px)',
								borderRadius: '16px',
								borderBottomLeftRadius: '0px',
							}}
						>
							<Typography sx={{ mb: 2 }} variant={TypographyVariants.baseSemiBold}>
								{categoryDetail.name}
							</Typography>
							<Typography variant={TypographyVariants.baseRegular}>{Strings.AI_CATEGORY_DESCRIPTION}</Typography>
						</Box>

						<Box sx={{ p: 3 }}>
							{categoryDetail.questions
								.slice(
									0,
									categoryDetail.questions.length <= 4 ? categoryDetail.questions.length : visibleQuestionsCount
								)
								.map(questionList => (
									<Grid container key={questionList.id}>
										<Box
											sx={{
												'maxWidth': '100%',
												'mb': 3,
												'& .MuiChip-label': {
													whiteSpace: 'normal',
													wordBreak: 'break-word',
													lineHeight: 1.4,
												},
											}}
										>
											<CustomChips
												label={questionList.question}
												dataTestId="suggested-sub-question"
												onClick={() => handleQuestionClick(questionList.question)}
												showIcon
												iconSx={{ mr: 1 }}
												maxLines={3}
											/>
										</Box>
									</Grid>
								))}
							<AIAssistantHistoryContainer
								ref={historyRef}
								autoScrollOnChildrenChange
								onlyWhenNearBottom
								nearBottomThreshold={120}
								smooth
								sx={{
									flex: 1,
									px: 2,
									display: 'flex',
									flexDirection: 'column',
									gap: 2,
									overflowY: 'auto',
									minHeight: 0,
								}}
							>
								{messages.map(m => (
									<React.Fragment key={m.id}>
										<AIQuestionBubble
											text={m.prompt}
											sx={{
												alignSelf: 'flex-end',
												borderRadius: '16px 16px 0 16px',
												backgroundColor: '#1E212C',
												maxWidth: { xs: '85%', md: '70%' },
												boxShadow: '0 4px 8px rgba(0,0,0,0.25)',
												marginBottom: '16px',
											}}
										/>
										{renderAssistantResponse(m)}
									</React.Fragment>
								))}
								<div ref={endRef} />
							</AIAssistantHistoryContainer>

							{loadingMoreQuestions && <CircularIndeterminate marginLeft="6px" />}

							{!loadingMoreQuestions &&
								categoryDetail.questions.length > 4 &&
								visibleQuestionsCount < categoryDetail.questions.length && (
									<Box
										onClick={handleSuggestMore}
										sx={{
											'display': 'flex',
											'alignItems': 'flex-start',
											'justifyContent': 'flex-end',
											'gap': 1,
											'cursor': 'pointer',
											'&:hover': { opacity: 0.8 },
										}}
									>
										<SvgIcon component={AISuggestIcon} fontSize="small" sx={{ mt: '4px' }} />
										<Typography variant={TypographyVariants.smallLight}>Suggest more questions</Typography>
									</Box>
								)}
						</Box>
					</>
				)}
			</Box>
		</Drawer>
	)
}
